import { useTranslation } from 'react-i18next';
import { X, Repeat } from 'lucide-react';
import { useAppointmentForm } from './appointment/hooks/useAppointmentForm';
import { useDealerSearch } from './appointment/hooks/useDealerSearch';
import { useSiteSearch } from './appointment/hooks/useSiteSearch';
import TeamInstallerSection from './appointment/sections/TeamInstallerSection';
import InterventionTypeSection from './appointment/sections/InterventionTypeSection';
import ClientSection from './appointment/sections/ClientSection';
import SiteSection from './appointment/sections/SiteSection';
import InstallationsSection from './appointment/sections/InstallationsSection';
import ScheduleSection from './appointment/sections/ScheduleSection';
import DocumentsUploadSection from './appointment/sections/DocumentsUploadSection';

import SalesforceJobSection from './appointment/sections/SalesforceJobSection';

const AppointmentModal = ({ appointment, teams, onClose, onSave }) => {
  const { t } = useTranslation();

  const {
    formData, setFormData,
    installations, addInstallation, removeInstallation, updateInstallation,
    loading, installers, calculateTotalDuration, handleSubmit,
    pendingSeriesSave, confirmSeriesScope, cancelSeriesScope,
    pendingFiles, setPendingFiles,
  } = useAppointmentForm(appointment, onSave, t);

  const dealerSearch = useDealerSearch(formData, setFormData);
  const siteState = useSiteSearch(formData, setFormData, appointment);

  const isAbsence = formData.intervention_type === 'absence';
  const isOther = formData.intervention_type === 'other';
  const isSimpleType = isAbsence || isOther; // nessun cliente/installazioni/SF
  // Show Salesforce Job section when creating, OR when editing a provisional appointment
  // (so the user can attach SF data when converting provisional -> confirmed)
  const showSalesforceJobSection = !isSimpleType && (
    !appointment ||
    appointment?.status === 'provisional' ||
    formData.confirmation_status === 'provisional'
  );

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-800">
            {appointment ? t('appointment.editTitle') : t('appointment.newTitle')}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="appointment-modal-close">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {showSalesforceJobSection && (
            <SalesforceJobSection formData={formData} setFormData={setFormData} installations={installations} updateInstallation={updateInstallation} />
          )}
          <TeamInstallerSection formData={formData} setFormData={setFormData} teams={teams} installers={installers} />
          <InterventionTypeSection formData={formData} setFormData={setFormData} />

          {!isSimpleType && (
            <>
              <ClientSection formData={formData} setFormData={setFormData} dealerSearch={dealerSearch} />
              <SiteSection formData={formData} setFormData={setFormData} siteSearch={siteState} />

              <InstallationsSection
                installations={installations}
                addInstallation={addInstallation}
                removeInstallation={removeInstallation}
                updateInstallation={updateInstallation}
                calculateTotalDuration={calculateTotalDuration}
                formData={formData}
                setFormData={setFormData}
              />
            </>
          )}

          <ScheduleSection formData={formData} setFormData={setFormData} appointment={appointment} calculateTotalDuration={calculateTotalDuration} />

          {/* Upload documenti in fase di creazione (in modifica si usa il dettaglio) */}
          {!appointment && !isSimpleType && (
            <DocumentsUploadSection pendingFiles={pendingFiles} setPendingFiles={setPendingFiles} />
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Note</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lojack-red"
              rows={3}
              data-testid="appointment-notes"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              data-testid="appointment-cancel">
              {t('common.cancel')}
            </button>
            <button type="submit" disabled={loading}
              className="px-6 py-2 bg-lojack-red text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50 flex items-center gap-2"
              data-testid="appointment-submit">
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                appointment ? t('common.save') : t('appointment.create')
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Recurrence Scope Dialog */}
      {pendingSeriesSave && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 z-[60] flex items-center justify-center p-4"
          data-testid="recurrence-scope-dialog"
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-5 border-b border-gray-200 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <Repeat size={20} className="text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">Modifica appuntamento ricorrente</h3>
            </div>
            <div className="p-5">
              <p className="text-sm text-gray-600 mb-4">
                Questo appuntamento fa parte di una serie ricorrente. A quali appuntamenti vuoi applicare le modifiche?
              </p>
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => confirmSeriesScope('this')}
                  disabled={loading}
                  className="w-full text-left px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-lojack-red transition disabled:opacity-50"
                  data-testid="recurrence-scope-this"
                >
                  <div className="font-medium text-gray-800">Solo questo</div>
                  <div className="text-xs text-gray-500 mt-0.5">Modifica solo questo appuntamento</div>
                </button>
                <button
                  type="button"
                  onClick={() => confirmSeriesScope('future')}
                  disabled={loading}
                  className="w-full text-left px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-lojack-red transition disabled:opacity-50"
                  data-testid="recurrence-scope-future"
                >
                  <div className="font-medium text-gray-800">Questo e successivi</div>
                  <div className="text-xs text-gray-500 mt-0.5">Modifica questo appuntamento e tutti i successivi della serie</div>
                </button>
                <button
                  type="button"
                  onClick={() => confirmSeriesScope('all')}
                  disabled={loading}
                  className="w-full text-left px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-lojack-red transition disabled:opacity-50"
                  data-testid="recurrence-scope-all"
                >
                  <div className="font-medium text-gray-800">Tutta la serie</div>
                  <div className="text-xs text-gray-500 mt-0.5">Modifica tutti gli appuntamenti della serie ricorrente</div>
                </button>
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end">
              <button
                type="button"
                onClick={cancelSeriesScope}
                disabled={loading}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
                data-testid="recurrence-scope-cancel"
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentModal;
